import { useEffect } from "react";
import { useAuth } from "@/auth/AuthContext";
import { useLocation } from "react-router-dom";
import { ParticleBackground } from "./ParticleBackground";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { AgentChatLayout } from "./AgentChatLayout";
import { CommandMenuProvider } from "./CommandMenu";
import { useChat } from "./chat/ChatProvider";
import { DemoChrome } from "@/demo/DemoChrome";

const UNCHROMED_ROUTES = ["/login", "/register", "/handoff", "/demo", "/demo-setup", "/try"];

function useChatShortcut() {
  const { toggleChat } = useChat();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "i" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleChat();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleChat]);
}

export function AppShell({ children }: { children: React.ReactNode }) {
  useChatShortcut();
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const unchromed = UNCHROMED_ROUTES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  if (!isAuthenticated || unchromed) {
    return (
      <CommandMenuProvider>
        <ParticleBackground prominent={pathname === "/" || unchromed} />
        <Header bare={unchromed} />
        {children}
      </CommandMenuProvider>
    );
  }

  return (
    <CommandMenuProvider>
      <ParticleBackground prominent={pathname === "/"} />
      <div className="flex h-screen min-h-0 flex-col">
        <DemoChrome />
        <Header />
        <div className="flex min-h-0 flex-1">
          <Sidebar />
          <main className="flex min-h-0 min-w-0 flex-1 flex-col">
            <AgentChatLayout>{children}</AgentChatLayout>
          </main>
        </div>
      </div>
    </CommandMenuProvider>
  );
}
